import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export type QuoteStatus =
  | "sent"
  | "viewed"
  | "accepted"
  | "rejected"
  | "expired"
  | "cancelled";

export interface QuoteLineItem {
  id?: string;
  quote_id?: string;
  label: string;
  description?: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price: number;
  display_order?: number;
}

export interface Quote {
  id: string;
  conversation_id: string | null;
  annonce_id: string | null;
  client_user_id: string | null;
  artisan_user_id: string;
  status: QuoteStatus;
  title: string;
  description: string;
  currency: string;
  total_amount: number;
  valid_until: string;
  estimated_intervention_delay: string | null;
  notes: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  line_items?: QuoteLineItem[];
}

export interface QuoteFormData {
  title: string;
  description: string;
  total_amount: number;
  valid_until: string;
  estimated_intervention_delay?: string;
  notes?: string;
  line_items?: Omit<QuoteLineItem, "id" | "quote_id">[];
}

/** Retourne true si le devis est expiré côté frontend (sans mettre à jour le statut en DB) */
export const isQuoteExpired = (quote: Quote) =>
  new Date(quote.valid_until) < new Date();

// ---------------------------------------------------------------------------
// useConversationQuotes — hook pour une conversation donnée
// ---------------------------------------------------------------------------
export const useConversationQuotes = (conversationId: string | null) => {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [lineItemsMap, setLineItemsMap] = useState<Record<string, QuoteLineItem[]>>({});
  const [loading, setLoading] = useState(false);

  const loadQuotes = useCallback(async () => {
    if (!conversationId || !user) return;
    const { data, error } = await supabase
      .from("quotes")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) return;
    const rows = (data ?? []) as Quote[];
    setQuotes(rows);

    // Charger les lignes de détail pour tous les devis
    if (rows.length > 0) {
      const ids = rows.map((q) => q.id);
      const { data: items } = await supabase
        .from("quote_line_items")
        .select("*")
        .in("quote_id", ids)
        .order("display_order", { ascending: true });

      if (items) {
        const map: Record<string, QuoteLineItem[]> = {};
        (items as QuoteLineItem[]).forEach((item) => {
          if (!map[item.quote_id!]) map[item.quote_id!] = [];
          map[item.quote_id!].push(item);
        });
        setLineItemsMap(map);
      }
    }
  }, [conversationId, user]);

  useEffect(() => {
    loadQuotes();
    if (!conversationId) return;

    const channel = supabase
      .channel(`quotes-conv-${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "quotes",
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => loadQuotes()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, loadQuotes]);

  /** Retourne le devis actif (sent ou viewed) pour cette conversation */
  const activeQuote = quotes.find((q) =>
    q.status === "sent" || q.status === "viewed"
  ) ?? null;

  /** L'artisan peut créer un nouveau devis si aucun devis en sent/viewed */
  const canCreateNewQuote = !activeQuote;

  const getConversationData = async () => {
    if (!conversationId) return null;
    const { data } = await supabase
      .from("conversations")
      .select("client_id, artisan_id, annonce_id")
      .eq("id", conversationId)
      .maybeSingle();
    return data;
  };

  const createQuote = async (form: QuoteFormData): Promise<Quote | null> => {
    if (!conversationId || !user) return null;
    if (!canCreateNewQuote) {
      toast({
        title: "Erreur",
        description: "Un devis est déjà en attente dans cette conversation.",
        variant: "destructive",
      });
      return null;
    }

    setLoading(true);
    try {
      const convo = await getConversationData();
      if (!convo) throw new Error("Conversation introuvable");

      const { data, error } = await supabase
        .from("quotes")
        .insert({
          conversation_id: conversationId,
          annonce_id: convo.annonce_id ?? null,
          client_user_id: convo.client_id,
          artisan_user_id: user.id,
          status: "sent",
          title: form.title,
          description: form.description,
          total_amount: form.total_amount,
          valid_until: new Date(form.valid_until).toISOString(),
          estimated_intervention_delay: form.estimated_intervention_delay || null,
          notes: form.notes || null,
          sent_at: new Date().toISOString(),
        } as any)
        .select()
        .single();

      if (error) throw error;
      const newQuote = data as Quote;

      // Insérer les lignes de détail si présentes
      if (form.line_items && form.line_items.length > 0) {
        const lineItemsPayload = form.line_items.map((item, idx) => ({
          quote_id: newQuote.id,
          label: item.label,
          description: item.description || null,
          quantity: item.quantity,
          unit: item.unit,
          unit_price: item.unit_price,
          total_price: item.total_price,
          display_order: idx,
        }));
        await supabase.from("quote_line_items").insert(lineItemsPayload as any);
      }

      // Forcer le rechargement des devis AVANT d'envoyer le message système
      // pour éviter la race condition : le message Realtime arrive avant le quotes Realtime
      await loadQuotes();

      // Message système dans la conversation
      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content: `📄 Nouveau devis : ${form.title}`,
        message_type: "devis",
        attachments: [{ type: "devis", quoteId: newQuote.id }],
      } as any);

      toast({ title: "Devis envoyé", description: "Le client a reçu votre devis." });
      return newQuote;
    } catch (e: any) {
      toast({
        title: "Erreur",
        description: e.message || "Impossible de créer le devis",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const acceptQuote = async (id: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc("accept_quote", { quote_id: id });
      if (error) throw error;

      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user!.id,
        content: "✅ Devis accepté",
        message_type: "devis",
        attachments: [{ type: "devis_update", quoteId: id, status: "accepted" }],
      } as any);

      toast({ title: "Devis accepté" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const rejectQuote = async (id: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc("reject_quote", { quote_id: id });
      if (error) throw error;

      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user!.id,
        content: "❌ Devis refusé",
        message_type: "devis",
        attachments: [{ type: "devis_update", quoteId: id, status: "rejected" }],
      } as any);

      toast({ title: "Devis refusé" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const cancelQuote = async (id: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc("cancel_quote", { quote_id: id });
      if (error) throw error;

      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user!.id,
        content: "Devis annulé",
        message_type: "devis",
        attachments: [{ type: "devis_update", quoteId: id, status: "cancelled" }],
      } as any);

      toast({ title: "Devis annulé" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const markQuoteViewed = async (id: string) => {
    try {
      await supabase.rpc("mark_quote_viewed", { quote_id: id });
    } catch {
      // silencieux
    }
  };

  const getQuoteWithLineItems = (quoteId: string): Quote | null => {
    const q = quotes.find((q) => q.id === quoteId) ?? null;
    if (!q) return null;
    return { ...q, line_items: lineItemsMap[quoteId] ?? [] };
  };

  return {
    quotes,
    activeQuote,
    canCreateNewQuote,
    loading,
    createQuote,
    acceptQuote,
    rejectQuote,
    cancelQuote,
    markQuoteViewed,
    getQuoteWithLineItems,
    loadQuotes,
  };
};

// ---------------------------------------------------------------------------
// useMyQuotes — hook pour la page MesDevis
// ---------------------------------------------------------------------------
export const useMyQuotes = () => {
  const { user } = useAuth();
  const [sentQuotes, setSentQuotes] = useState<Quote[]>([]);
  const [receivedQuotes, setReceivedQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [sentRes, receivedRes] = await Promise.all([
        supabase
          .from("quotes")
          .select("*")
          .eq("artisan_user_id", user.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("quotes")
          .select("*")
          .eq("client_user_id", user.id)
          .order("created_at", { ascending: false }),
      ]);

      setSentQuotes((sentRes.data ?? []) as Quote[]);
      setReceivedQuotes((receivedRes.data ?? []) as Quote[]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return { sentQuotes, receivedQuotes, loading, reload: load };
};
